import { useEffect, useRef, useState } from 'react';
import { useStripePromise } from '@frontend/payments-stripe-wrapper';
import type {
  PaymentMethodSetupApi,
  PaymentMethodSetupEventHandler,
  PaymentMethodSetupGatewayConfig,
  PaymentMethodSetupKind,
  SavedPaymentMethod,
} from '../types';
import { fetchSavedPaymentMethod, getPaymentMethodId } from '../utils/poll-payment-method';
import {
  clearSetupCardholderName,
  clearSetupRedirectParams,
  readSetupCardholderName,
  readStripeSetupRedirect,
} from '../utils/setup-redirect';

export type UseCompletePaymentMethodSetupRedirectArgs = {
  kind: PaymentMethodSetupKind;
  setupKey: string;
  enabled: boolean;
  api: PaymentMethodSetupApi;
  gatewayConfig: PaymentMethodSetupGatewayConfig;
  onComplete: (method: SavedPaymentMethod) => void;
  onEvent?: PaymentMethodSetupEventHandler;
};

export type PaymentMethodSetupRedirectStatus = 'idle' | 'inProgress' | 'error';

/** Finishes a setup for `setupKey` that returned from a processor redirect. */
export const useCompletePaymentMethodSetupRedirect = ({
  kind,
  setupKey,
  enabled,
  api,
  gatewayConfig,
  onComplete,
  onEvent,
}: UseCompletePaymentMethodSetupRedirectArgs) => {
  const publishableKey = gatewayConfig.stripe?.publishableKey ?? '';
  const [redirect] = useState(() =>
    enabled && publishableKey && typeof window !== 'undefined' ? readStripeSetupRedirect(setupKey) : undefined
  );
  // An empty key skips loading the processor SDK when there is nothing to complete.
  const { stripePromise } = useStripePromise({ stripeExpressKey: redirect ? publishableKey : '' });
  const [status, setStatus] = useState<PaymentMethodSetupRedirectStatus>(redirect ? 'inProgress' : 'idle');
  const handledRef = useRef(false);

  useEffect(() => {
    if (!redirect || handledRef.current) return;
    handledRef.current = true;

    const complete = async () => {
      try {
        const stripe = await stripePromise;
        if (!stripe) throw new Error('processor_not_ready');

        const { setupIntent, error } = await stripe.retrieveSetupIntent(redirect.clientSecret);
        const paymentMethodId = getPaymentMethodId(setupIntent?.payment_method);
        if (error || setupIntent?.status !== 'succeeded' || !paymentMethodId) {
          throw new Error(error?.code ?? setupIntent?.status ?? 'missing_payment_method');
        }

        const { method, error: fetchError } = await fetchSavedPaymentMethod({
          getPaymentMethod: api.getPaymentMethod,
          paymentMethodId,
          kind,
        });
        if (fetchError) throw new Error(fetchError);

        onEvent?.('redirect_completed');
        onComplete(method.kind === 'card' ? { ...method, nameOnCard: readSetupCardholderName(setupKey) } : method);
        setStatus('idle');
      } catch (error) {
        onEvent?.('redirect_error', { code: error instanceof Error ? error.message : 'unexpected' });
        setStatus('error');
      } finally {
        clearSetupRedirectParams();
        clearSetupCardholderName(setupKey);
      }
    };

    void complete();
  }, [api, kind, onComplete, onEvent, redirect, setupKey, stripePromise]);

  return { redirectStatus: status };
};
